import { Box } from '@chakra-ui/react';
import { useResource, useResources } from 'datx-jsonapi-react';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FadeInModalContext } from '@/components/layouts/fadeInModalContext';
import { EventHeader } from '@/components/ui/shared/headers/EventHeader/EventHeader';
import { LibLynxCounterDefaults } from '@/components/ui/shared/utilities/LibLynxCounterDefaults/LibLynxCoutnerDefaults';
import { LibraryRedirect } from '@/components/ui/shared/utilities/LibraryRedirect/LibraryRedirect';
import { useAnalytics } from '@/context/Analytics';
import { OverlayType } from '@/enums/index';
import { IToastType } from '@/enums/ToastType';
import { useLogoutMutation } from '@/hooks/useLogoutMutation';
import { useQueryParams } from '@/hooks/useQueryParams';
import useSession from '@/hooks/useSession';
import { useToast } from '@/hooks/useToast';
import { queryThinEvent } from '@/queries/thin-events';
import { queryThinProfilesByUserId } from '@/queries/thin-profiles';
import { StringParam } from '@/utils/url-query-string';

export const EventAppLayout: FC<{ children?: ReactNode }> = ({ children }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const [showModalType, setShowModalType] = useState(OverlayType.NONE);

  const { eventId, router } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data: event } = useResource(queryThinEvent(eventId));

  const onLogoutSuccess = () => {
    analytics.track('user_logout');
    toast({ status: IToastType.SUCCESS, title: t('action.loggedOut') });
  };

  const onLogoutError = () => {
    toast({ status: IToastType.ERROR, title: t('action.loggedOutError') });
  };

  const { user: currentUser } = useSession();

  const [logOut] = useLogoutMutation({ onSuccess: onLogoutSuccess, onFailure: onLogoutError });

  const { data: currentUserProfileData } = useResources(queryThinProfilesByUserId(currentUser?.id));

  const currentUserProfile = currentUserProfileData?.[0];

  useEffect(() => {
    if (event) {
      analytics.page('', {
        event: event.name,
        event_id: event.id,
        event_state: event.state,
      });
    }
  }, [event, analytics]);

  // TODO: FadeInModalContext should've been a Portal
  return (
    // <TwilioChatProvider>
    <FadeInModalContext.Provider
      value={{
        showModalType: { get: showModalType, set: setShowModalType },
      }}
    >
      <LibLynxCounterDefaults />
      {/* Only encapsulates redirection in order no to clutter the layout component */}
      <LibraryRedirect event={event} />

      <EventHeader
        event={event}
        currentUserProfile={currentUserProfile}
        onLogout={() => logOut(null)}
        zIndex="banner"
      />

      {/* TODO: full-screen legacy code; remove padding and header to make place for full screen */}
      <Box as="main" pl={{ lg: '4.5rem' }}>
        {children}
      </Box>
    </FadeInModalContext.Provider>
    // </TwilioChatProvider>
  );
};
